import React from 'react';
import { motion } from 'framer-motion';
import { X, Check, Sparkles } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export function Comparison() {
  const { t } = useLanguage();
  const traditional = t('comparison.traditional');
  const dathent = t('comparison.dathent');

  return (
    <section id="comparison" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-800/20 via-slate-950 to-slate-950" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-red-400 font-mono text-sm tracking-wider uppercase mb-4 block">
            The truth
          </span>
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-4">
            {t('comparison.title')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('comparison.subtitle')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Traditional Agency - Faded/Bad */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
            className="relative"
            data-testid="comparison-traditional"
          >
            <div className="bg-slate-900/30 border border-slate-800/50 rounded-3xl p-8 md:p-10 opacity-70 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-500">
              <div className="absolute -top-4 left-8 px-4 py-1 bg-slate-800 rounded-full text-sm text-slate-400 font-mono">
                Old way
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold mb-8 text-slate-400">
                {traditional.title}
              </h3>
              <ul className="space-y-5">
                {traditional.items.map((item, index) => (
                  <motion.li 
                    key={index} 
                    className="flex items-start gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center shrink-0 mt-0.5">
                      <X className="w-4 h-4 text-red-400" />
                    </div>
                    <span className="text-slate-500 text-lg">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Dathent Approach - Vibrant/Good */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative"
            data-testid="comparison-dathent"
          >
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-primary rounded-3xl p-8 md:p-10 relative shadow-2xl shadow-primary/20">
              {/* Glow effect */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/30 blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/20 blur-3xl" />
              
              <div className="absolute -top-4 left-8 px-4 py-1 bg-primary rounded-full text-sm text-white font-mono flex items-center gap-2">
                <Sparkles size={14} />
                New way
              </div>
              
              <h3 className="font-heading text-2xl md:text-3xl font-bold mb-8 text-white relative">
                {dathent.title}
              </h3>
              <ul className="space-y-5 relative">
                {dathent.items.map((item, index) => (
                  <motion.li 
                    key={index} 
                    className="flex items-start gap-4"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-slate-200 text-lg">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
